import React, { useCallback, useContext, useRef } from 'react';

import { AppContext } from '@r-app/app-context';
import { SkuAnnotationArrowhead, SkuAnnotationLineStyle, SkuAnnotationType } from '@rta/rta-blueprint-library';
import type { SkuAnnotationTool } from './sku-annotation-toolbar';

export type SkuAnnotationInteractionLayerProps = {
	activeTool: SkuAnnotationTool;
	onAnnotationAdded?: (id: string) => void;
};

const MIN_TEXTBOX_WIDTH = 12;
const MIN_TEXTBOX_HEIGHT = 5;
const MIN_DRAG_THRESHOLD = 2;
const MIN_LINE_LENGTH = 2;
const MIN_SHAPE_SIZE = 3;

/**
 * All drawing state is tracked via refs (not useState) to avoid re-renders during
 * mouse interactions. Re-renders during drawing can trigger viewport recalculations
 * (scale jumping) and break preview element refs due to React reconciliation.
 */
export const SkuAnnotationInteractionLayer = (props: SkuAnnotationInteractionLayerProps): React.ReactElement => {
	const { activeTool, onAnnotationAdded } = props;
	const context = useContext(AppContext);
	const controller = context?.controller;
	const kitchen = controller?.kitchen;

	// Drawing state tracked via refs — no re-renders during draw
	const isDrawingRef = useRef(false);
	const drawModeRef = useRef<'none' | 'textbox' | 'doodle' | 'line' | 'rectangle' | 'ellipse'>('none');
	const currentPointsRef = useRef<Array<{ x: number; y: number }>>([]);
	const startRef = useRef<{ x: number; y: number } | null>(null);
	const lastSvgPosRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

	// SVG element refs for direct DOM manipulation (no React re-render needed)
	const drawingPathRef = useRef<SVGPathElement | null>(null);
	const textBoxPreviewRef = useRef<SVGRectElement | null>(null);
	const linePreviewRef = useRef<SVGLineElement | null>(null);
	const rectPreviewRef = useRef<SVGRectElement | null>(null);
	const ellipsePreviewRef = useRef<SVGEllipseElement | null>(null);

	// Convert screen coordinates to SVG coordinates
	const screenToSvg = useCallback((e: React.MouseEvent<SVGRectElement>): { x: number; y: number } => {
		const svg = (e.target as SVGRectElement).ownerSVGElement;
		if (!svg) return { x: 0, y: 0 };
		const pt = svg.createSVGPoint();
		pt.x = e.clientX;
		pt.y = e.clientY;
		const svgPt = pt.matrixTransform(svg.getScreenCTM()?.inverse());
		return { x: svgPt.x, y: svgPt.y };
	}, []);

	const handleMouseDown = useCallback((e: React.MouseEvent<SVGRectElement>): void => {
		if (activeTool === 'select' || !kitchen) return;

		const pos = screenToSvg(e);
		lastSvgPosRef.current = pos;
		startRef.current = pos;
		isDrawingRef.current = true;
		drawModeRef.current = activeTool as typeof drawModeRef.current;

		if (activeTool === 'doodle') {
			currentPointsRef.current = [{ x: pos.x, y: pos.y }];
		}

		// Initialize line preview at the start point
		if (activeTool === 'line' && linePreviewRef.current) {
			linePreviewRef.current.setAttribute('x1', String(pos.x));
			linePreviewRef.current.setAttribute('y1', String(pos.y));
			linePreviewRef.current.setAttribute('x2', String(pos.x));
			linePreviewRef.current.setAttribute('y2', String(pos.y));
			linePreviewRef.current.setAttribute('visibility', 'visible');
		}

		// Initialize textbox preview
		if (activeTool === 'textbox' && textBoxPreviewRef.current) {
			textBoxPreviewRef.current.setAttribute('x', String(pos.x));
			textBoxPreviewRef.current.setAttribute('y', String(pos.y));
			textBoxPreviewRef.current.setAttribute('width', '0');
			textBoxPreviewRef.current.setAttribute('height', '0');
			textBoxPreviewRef.current.setAttribute('visibility', 'visible');
		}

		// Initialize rect preview
		if (activeTool === 'rectangle' && rectPreviewRef.current) {
			rectPreviewRef.current.setAttribute('x', String(pos.x));
			rectPreviewRef.current.setAttribute('y', String(pos.y));
			rectPreviewRef.current.setAttribute('width', '0');
			rectPreviewRef.current.setAttribute('height', '0');
			rectPreviewRef.current.setAttribute('visibility', 'visible');
		}

		// Initialize ellipse preview
		if (activeTool === 'ellipse' && ellipsePreviewRef.current) {
			ellipsePreviewRef.current.setAttribute('cx', String(pos.x));
			ellipsePreviewRef.current.setAttribute('cy', String(pos.y));
			ellipsePreviewRef.current.setAttribute('rx', '0');
			ellipsePreviewRef.current.setAttribute('ry', '0');
			ellipsePreviewRef.current.setAttribute('visibility', 'visible');
		}

		// Initialize doodle preview
		if (activeTool === 'doodle' && drawingPathRef.current) {
			drawingPathRef.current.setAttribute('d', `M ${pos.x} ${pos.y}`);
			drawingPathRef.current.setAttribute('visibility', 'visible');
		}
	}, [activeTool, kitchen, screenToSvg]);

	const handleMouseMove = useCallback((e: React.MouseEvent<SVGRectElement>): void => {
		if (!isDrawingRef.current) return;

		const pos = screenToSvg(e);
		lastSvgPosRef.current = pos;
		const start = startRef.current;
		if (!start) return;
		const mode = drawModeRef.current;

		// TextBox preview
		if (mode === 'textbox' && textBoxPreviewRef.current) {
			const x = Math.min(pos.x, start.x);
			const y = Math.min(pos.y, start.y);
			const w = Math.abs(pos.x - start.x);
			const h = Math.abs(pos.y - start.y);
			textBoxPreviewRef.current.setAttribute('x', String(x));
			textBoxPreviewRef.current.setAttribute('y', String(y));
			textBoxPreviewRef.current.setAttribute('width', String(w));
			textBoxPreviewRef.current.setAttribute('height', String(h));
			return;
		}

		// Doodle preview
		if (mode === 'doodle' && drawingPathRef.current) {
			currentPointsRef.current.push({ x: pos.x, y: pos.y });
			const d = currentPointsRef.current.reduce((path, pt, i) =>
				i === 0 ? `M ${pt.x} ${pt.y}` : `${path} L ${pt.x} ${pt.y}`, '');
			drawingPathRef.current.setAttribute('d', d);
			return;
		}

		// Line preview
		if (mode === 'line' && linePreviewRef.current) {
			linePreviewRef.current.setAttribute('x2', String(pos.x));
			linePreviewRef.current.setAttribute('y2', String(pos.y));
			return;
		}

		// Rectangle preview
		if (mode === 'rectangle' && rectPreviewRef.current) {
			const x = Math.min(pos.x, start.x);
			const y = Math.min(pos.y, start.y);
			const w = Math.abs(pos.x - start.x);
			const h = Math.abs(pos.y - start.y);
			rectPreviewRef.current.setAttribute('x', String(x));
			rectPreviewRef.current.setAttribute('y', String(y));
			rectPreviewRef.current.setAttribute('width', String(w));
			rectPreviewRef.current.setAttribute('height', String(h));
			return;
		}

		// Ellipse preview
		if (mode === 'ellipse' && ellipsePreviewRef.current) {
			const cx = (pos.x + start.x) / 2;
			const cy = (pos.y + start.y) / 2;
			const rx = Math.abs(pos.x - start.x) / 2;
			const ry = Math.abs(pos.y - start.y) / 2;
			ellipsePreviewRef.current.setAttribute('cx', String(cx));
			ellipsePreviewRef.current.setAttribute('cy', String(cy));
			ellipsePreviewRef.current.setAttribute('rx', String(rx));
			ellipsePreviewRef.current.setAttribute('ry', String(ry));
		}
	}, [screenToSvg]);

	const hideAllPreviews = useCallback((): void => {
		textBoxPreviewRef.current?.setAttribute('visibility', 'hidden');
		linePreviewRef.current?.setAttribute('visibility', 'hidden');
		rectPreviewRef.current?.setAttribute('visibility', 'hidden');
		ellipsePreviewRef.current?.setAttribute('visibility', 'hidden');
		drawingPathRef.current?.setAttribute('visibility', 'hidden');
	}, []);

	const handleMouseUp = useCallback((): void => {
		if (!isDrawingRef.current || !kitchen) return;

		const mode = drawModeRef.current;
		const start = startRef.current;
		const pos = lastSvgPosRef.current;

		isDrawingRef.current = false;
		drawModeRef.current = 'none';
		startRef.current = null;
		hideAllPreviews();

		// TextBox creation
		if (mode === 'textbox' && start) {
			const rawW = Math.abs(pos.x - start.x);
			const rawH = Math.abs(pos.y - start.y);
			const isDrag = rawW > MIN_DRAG_THRESHOLD || rawH > MIN_DRAG_THRESHOLD;
			const width = isDrag ? Math.max(MIN_TEXTBOX_WIDTH, rawW) : MIN_TEXTBOX_WIDTH;
			const height = isDrag ? Math.max(MIN_TEXTBOX_HEIGHT, rawH) : MIN_TEXTBOX_HEIGHT;
			const x = Math.min(pos.x, start.x);
			const y = Math.min(pos.y, start.y);

			const id = kitchen.skuAnnotations.add({
				type: SkuAnnotationType.TextBox,
				x, y, width, height,
				text: '',
				fontSize: 1.5,
				color: '#333333',
				backgroundColor: '#ffffff',
				textColor: '#333333',
				fontFamily: 'Arial'
			});
			if (typeof id === 'string' && onAnnotationAdded) onAnnotationAdded(id);
			return;
		}

		// Doodle creation
		if (mode === 'doodle') {
			const points = currentPointsRef.current;
			if (points.length >= 2) {
				const minX = Math.min(...points.map((p) => p.x));
				const minY = Math.min(...points.map((p) => p.y));
				const id = kitchen.skuAnnotations.add({
					type: SkuAnnotationType.Doodle,
					x: minX,
					y: minY,
					width: 0,
					height: 0,
					text: '',
					fontSize: 19,
					color: '#e53e3e',
					strokeWidth: 0.3,
					lineStyle: SkuAnnotationLineStyle.Solid,
					arrowhead: SkuAnnotationArrowhead.None,
					points: points.map((p) => ({ x: p.x, y: p.y }))
				});
				if (typeof id === 'string' && onAnnotationAdded) onAnnotationAdded(id);
			}
			currentPointsRef.current = [];
			drawingPathRef.current?.setAttribute('d', '');
			return;
		}

		// Line creation
		if (mode === 'line' && start) {
			const dx = pos.x - start.x;
			const dy = pos.y - start.y;
			const length = Math.sqrt(dx * dx + dy * dy);

			if (length >= MIN_LINE_LENGTH) {
				const id = kitchen.skuAnnotations.add({
					type: SkuAnnotationType.Line,
					x: start.x,
					y: start.y,
					x2: pos.x,
					y2: pos.y,
					width: 0,
					height: 0,
					text: '',
					fontSize: 19,
					color: '#333333',
					strokeWidth: 0.3,
					lineStyle: SkuAnnotationLineStyle.Solid,
					arrowhead: SkuAnnotationArrowhead.None
				});
				if (typeof id === 'string' && onAnnotationAdded) onAnnotationAdded(id);
			}
			return;
		}

		// Rectangle creation
		if (mode === 'rectangle' && start) {
			const width = Math.abs(pos.x - start.x);
			const height = Math.abs(pos.y - start.y);

			if (width >= MIN_SHAPE_SIZE && height >= MIN_SHAPE_SIZE) {
				const x = Math.min(pos.x, start.x);
				const y = Math.min(pos.y, start.y);
				const id = kitchen.skuAnnotations.add({
					type: SkuAnnotationType.Rectangle,
					x, y, width, height,
					text: '',
					fontSize: 19,
					color: '#333333',
					strokeWidth: 0.3,
					lineStyle: SkuAnnotationLineStyle.Solid,
					arrowhead: SkuAnnotationArrowhead.None
				});
				if (typeof id === 'string' && onAnnotationAdded) onAnnotationAdded(id);
			}
			return;
		}

		// Ellipse creation
		if (mode === 'ellipse' && start) {
			const width = Math.abs(pos.x - start.x);
			const height = Math.abs(pos.y - start.y);

			if (width >= MIN_SHAPE_SIZE && height >= MIN_SHAPE_SIZE) {
				const x = Math.min(pos.x, start.x);
				const y = Math.min(pos.y, start.y);
				const id = kitchen.skuAnnotations.add({
					type: SkuAnnotationType.Ellipse,
					x, y, width, height,
					text: '',
					fontSize: 19,
					color: '#333333',
					strokeWidth: 0.3,
					lineStyle: SkuAnnotationLineStyle.Solid,
					arrowhead: SkuAnnotationArrowhead.None
				});
				if (typeof id === 'string' && onAnnotationAdded) onAnnotationAdded(id);
			}
		}
	}, [kitchen, onAnnotationAdded, hideAllPreviews]);

	if (activeTool === 'select') return <></>;

	return (
		<g type="sku-annotation-interaction-layer">
			{/* Full viewport capture rect */}
			<rect
				x={-100000} y={-100000} width={200000} height={200000}
				fill="transparent"
				style={{
					pointerEvents: 'auto',
					cursor: 'crosshair'
				}}
				onMouseDown={handleMouseDown}
				onMouseMove={handleMouseMove}
				onMouseUp={handleMouseUp}
				onMouseLeave={handleMouseUp}
			/>
			{/* All preview elements are always mounted but hidden via visibility.
			    Direct DOM manipulation avoids React re-renders during drawing. */}
			<rect
				ref={textBoxPreviewRef}
				x={0} y={0} width={0} height={0}
				fill="rgba(49, 130, 206, 0.08)"
				stroke="#3182ce"
				strokeWidth={0.3}
				strokeDasharray="1,1"
				visibility="hidden"
				style={{ pointerEvents: 'none' }}
			/>
			<path
				ref={drawingPathRef}
				d="" fill="none" stroke="#e53e3e"
				strokeWidth={0.3} strokeLinecap="round" strokeLinejoin="round"
				visibility="hidden"
				style={{ pointerEvents: 'none' }}
			/>
			<line
				ref={linePreviewRef}
				x1={0} y1={0} x2={0} y2={0}
				stroke="#3182ce"
				strokeWidth={0.3}
				strokeDasharray="1,1"
				visibility="hidden"
				style={{ pointerEvents: 'none' }}
			/>
			<rect
				ref={rectPreviewRef}
				x={0} y={0} width={0} height={0}
				fill="none"
				stroke="#3182ce"
				strokeWidth={0.3}
				strokeDasharray="1,1"
				visibility="hidden"
				style={{ pointerEvents: 'none' }}
			/>
			<ellipse
				ref={ellipsePreviewRef}
				cx={0} cy={0} rx={0} ry={0}
				fill="none"
				stroke="#3182ce"
				strokeWidth={0.3}
				strokeDasharray="1,1"
				visibility="hidden"
				style={{ pointerEvents: 'none' }}
			/>
		</g>
	);
};
